/**
 * FERP PDF Annotation List Panel (DRR-079)
 *
 * List of all annotations with filter/sort. Click to navigate to annotation page.
 */

import { useMemo, useState } from 'react';
import {
  Stack,
  Box,
  Text,
  UnstyledButton,
  Badge,
  Select,
  Group,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import { IconTrash, IconEye } from '@tabler/icons-react';
import { usePdfSuite } from './PdfSuiteContext';
import type { PdfAnnotationType } from './types';

const TYPE_LABELS: Record<PdfAnnotationType, string> = {
  highlight: 'Highlight',
  text: 'Comment',
  stamp: 'Stamp',
  ink: 'Drawing',
  shape: 'Shape',
  sticky: 'Sticky Note',
  redaction: 'Redaction',
};

const TYPE_COLORS: Record<PdfAnnotationType, string> = {
  highlight: 'yellow',
  text: 'orange',
  stamp: 'green',
  ink: 'blue',
  shape: 'violet',
  sticky: 'yellow',
  redaction: 'red',
};

type SortBy = 'page' | 'type' | 'date';

export function PdfAnnotationListPanel() {
  const { state, setCurrentPage, selectAnnotation, removeAnnotation } = usePdfSuite();
  const { annotations, selectedAnnotationId } = state;
  const [filterType, setFilterType] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortBy>('page');

  const filtered = useMemo(() => {
    let items = [...annotations];
    if (filterType) {
      items = items.filter((a) => a.type === filterType);
    }
    items.sort((a, b) => {
      if (sortBy === 'page') return a.page - b.page;
      if (sortBy === 'type') return a.type.localeCompare(b.type);
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
    return items;
  }, [annotations, filterType, sortBy]);

  const typeOptions = [
    { value: '', label: 'All types' },
    ...Object.entries(TYPE_LABELS).map(([value, label]) => ({ value, label })),
  ];

  return (
    <Stack gap="xs" p="xs">
      <Group gap="xs">
        <Select
          size="xs"
          w={100}
          data={typeOptions}
          value={filterType ?? ''}
          onChange={(v) => setFilterType(v || null)}
          placeholder="Filter"
          allowDeselect={false}
        />
        <Select
          size="xs"
          w={80}
          data={[
            { value: 'page', label: 'Page' },
            { value: 'type', label: 'Type' },
            { value: 'date', label: 'Date' },
          ]}
          value={sortBy}
          onChange={(v) => v && setSortBy(v as SortBy)}
          allowDeselect={false}
        />
      </Group>

      <Text size="xs" c="dimmed">
        {filtered.length} annotation{filtered.length !== 1 ? 's' : ''}
      </Text>

      {filtered.length === 0 ? (
        <Text size="xs" c="dimmed" ta="center" py="md">
          No annotations
        </Text>
      ) : (
        <Stack gap={4}>
          {filtered.map((ann) => (
            <UnstyledButton
              key={ann.id}
              onClick={() => {
                selectAnnotation(ann.id);
                setCurrentPage(ann.page);
              }}
              style={{
                padding: 8,
                borderRadius: 4,
                border:
                  selectedAnnotationId === ann.id
                    ? '2px solid var(--mantine-color-blue-5)'
                    : '1px solid var(--mantine-color-gray-3)',
                backgroundColor:
                  selectedAnnotationId === ann.id
                    ? 'var(--mantine-color-blue-0)'
                    : 'white',
              }}
            >
              <Group gap="xs" wrap="nowrap" justify="space-between">
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Group gap={4}>
                    <Badge size="xs" color={TYPE_COLORS[ann.type]} variant="light">
                      {TYPE_LABELS[ann.type]}
                    </Badge>
                    <Text size="xs" c="dimmed">
                      p.{ann.page}
                    </Text>
                  </Group>
                  {ann.content && (
                    <Text size="xs" lineClamp={1} mt={2}>
                      {ann.content}
                    </Text>
                  )}
                  <Text size="xs" c="dimmed" mt={2}>
                    {ann.createdBy}
                  </Text>
                </Box>
                <Group gap={4}>
                  <Tooltip label="Go to page">
                    <ActionIcon
                      variant="subtle"
                      size="xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentPage(ann.page);
                      }}
                    >
                      <IconEye size={12} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Delete">
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      size="xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeAnnotation(ann.id);
                      }}
                    >
                      <IconTrash size={12} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Group>
            </UnstyledButton>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
